import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import classes from './opportunitySearch.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Pipeline {
  id: string;
  name: string;
}

interface CustomFieldDef {
  id: string;
  name: string;
  fieldKey: string;
  objectType: string; // "opportunity" | "contact" | etc.
}

interface ApiContact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  type: string;
  company_name: string | null;
  business_id: string | null;
  business?: { id: string; name: string | null } | null;
}

interface ApiOpportunity {
  id: string;
  location_id?: string | null;
  name: string | null;
  monetary_value: number | null;
  status: string | null;
  pipeline_id?: string | null;
  source?: string | null;
  date_added?: string | null;
  created_at?: string | null;
  contact?: ApiContact | null;
  referred_by?: ApiContact | null;
  referral_fee?: { referral_partner?: ApiContact | null } | null;
  pipeline?: { id: string; name: string } | null;
  stage?: { id: string; name: string } | null;
  custom_fields?: { id: string; field_value?: string | null }[];
}

/** Flat row used for display & sorting. Every value is a string for simplicity. */
interface FlatRow {
  _id: string;
  _locationId: string | null;
  _pipelineId: string | null;
  _contactId: string | null;
  [key: string]: string | number | null;
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

type ColumnCategory = 'Opportunity' | 'Contact' | 'Referral';

interface ColumnDef {
  key: string;
  label: string;
  category: ColumnCategory;
  /** Extract display value from the raw API opportunity */
  accessor: (opp: ApiOpportunity) => string | number;
  /** Render style hint */
  render?: 'currency' | 'date' | 'badge' | 'status' | 'link-opp' | 'link-contact';
}

const buildContactName = (c?: ApiContact | null): string => {
  if (!c) return '\u2014';
  const display = c.display_name?.trim();
  if (display) return display;
  const parts = [c.first_name, c.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '\u2014';
};

const STATIC_COLUMNS: ColumnDef[] = [
  // -- Opportunity fields --
  { key: 'opp_name', label: 'Opportunity Name', category: 'Opportunity', accessor: (o) => o.name || 'Untitled', render: 'link-opp' },
  { key: 'opp_value', label: 'Value', category: 'Opportunity', accessor: (o) => Number(o.monetary_value ?? 0), render: 'currency' },
  { key: 'opp_status', label: 'Status', category: 'Opportunity', accessor: (o) => (o.status || 'open').toUpperCase(), render: 'status' },
  { key: 'opp_pipeline', label: 'Pipeline', category: 'Opportunity', accessor: (o) => o.pipeline?.name || '\u2014', render: 'badge' },
  { key: 'opp_stage', label: 'Stage', category: 'Opportunity', accessor: (o) => o.stage?.name || '\u2014', render: 'badge' },
  { key: 'opp_source', label: 'Source', category: 'Opportunity', accessor: (o) => o.source?.trim() || '\u2014' },
  { key: 'opp_created', label: 'Created', category: 'Opportunity', accessor: (o) => o.date_added || o.created_at || '', render: 'date' },

  // -- Contact fields --
  { key: 'contact_name', label: 'Contact Name', category: 'Contact', accessor: (o) => buildContactName(o.contact), render: 'link-contact' },
  { key: 'contact_first', label: 'Contact First Name', category: 'Contact', accessor: (o) => o.contact?.first_name || '\u2014' },
  { key: 'contact_last', label: 'Contact Last Name', category: 'Contact', accessor: (o) => o.contact?.last_name || '\u2014' },
  { key: 'contact_email', label: 'Contact Email', category: 'Contact', accessor: (o) => o.contact?.email || '\u2014' },
  { key: 'contact_phone', label: 'Contact Phone', category: 'Contact', accessor: (o) => o.contact?.phone || '\u2014' },
  { key: 'contact_type', label: 'Contact Type', category: 'Contact', accessor: (o) => o.contact?.type || '\u2014' },
  { key: 'contact_company', label: 'Contact Company', category: 'Contact', accessor: (o) => o.contact?.company_name || o.contact?.business?.name || '\u2014' },

  // -- Referral fields --
  { key: 'ref_name', label: 'Referred By', category: 'Referral', accessor: (o) => buildContactName(o.referred_by) },
  { key: 'ref_email', label: 'Referrer Email', category: 'Referral', accessor: (o) => o.referred_by?.email || '\u2014' },
  { key: 'ref_phone', label: 'Referrer Phone', category: 'Referral', accessor: (o) => o.referred_by?.phone || '\u2014' },
  { key: 'ref_company', label: 'Referrer Company', category: 'Referral', accessor: (o) => o.referred_by?.company_name || o.referred_by?.business?.name || '\u2014' },
  { key: 'ref_partner', label: 'Referral Partner', category: 'Referral', accessor: (o) => buildContactName(o.referral_fee?.referral_partner) },
  { key: 'ref_partner_company', label: 'Referral Partner Company', category: 'Referral', accessor: (o) => o.referral_fee?.referral_partner?.company_name || o.referral_fee?.referral_partner?.business?.name || '\u2014' },
];

const STATIC_COLUMN_MAP = new Map(STATIC_COLUMNS.map((c) => [c.key, c]));

/** Build a ColumnDef for a GHL custom field definition. */
const buildCustomFieldColumn = (cf: CustomFieldDef): ColumnDef => {
  const cfId = cf.id;
  const category: ColumnCategory = cf.objectType === 'contact' ? 'Contact' : 'Opportunity';
  return {
    key: `cf_${cfId}`,
    label: cf.name,
    category,
    accessor: (opp: ApiOpportunity) => {
      const match = opp.custom_fields?.find((f) => f.id === cfId);
      return match?.field_value || '\u2014';
    },
  };
};

const DEFAULT_COLUMNS = ['opp_name', 'opp_value', 'contact_name', 'contact_email', 'opp_pipeline', 'opp_stage', 'opp_created', 'opp_status'];

const STORAGE_KEY = 'rp_opp_search_columns';

/** Load saved columns. Custom field keys (cf_*) are allowed even if not yet in the map. */
const loadSavedColumns = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        Array.isArray(parsed) &&
        parsed.length > 0 &&
        parsed.every((k: unknown) => typeof k === 'string' && (STATIC_COLUMN_MAP.has(k as string) || (k as string).startsWith('cf_')))
      ) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return DEFAULT_COLUMNS;
};

const saveColumns = (keys: string[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // ignore
  }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const GHL_BASE_URL = 'https://crm.rocketplantech.com/v2/location';

const PAGE_SIZE = 25;

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatDate = (dateString: string): string => {
  if (!dateString) return '\u2014';
  try {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '\u2014';
  }
};

const buildGhlOpportunityUrl = (locId: string, opportunityId: string): string =>
  `${GHL_BASE_URL}/${locId}/opportunities/${opportunityId}?tab=Opportunity+Details`;

const buildGhlContactUrl = (locId: string, contactId: string): string =>
  `${GHL_BASE_URL}/${locId}/contacts/detail/${contactId}`;

/** Flatten an API opportunity into a row keyed by column keys. */
const flattenOpportunity = (opp: ApiOpportunity, columns: ColumnDef[]): FlatRow => {
  const row: FlatRow = {
    _id: opp.id,
    _locationId: opp.location_id || null,
    _pipelineId: opp.pipeline_id || opp.pipeline?.id || null,
    _contactId: opp.contact?.id || null,
  };
  columns.forEach((col) => {
    row[col.key] = col.accessor(opp);
  });
  return row;
};

// ---------------------------------------------------------------------------
// Column Picker component
// ---------------------------------------------------------------------------

interface ColumnPickerProps {
  allColumns: ColumnDef[];
  selected: string[];
  onChange: (keys: string[]) => void;
}

function ColumnPicker({ allColumns, selected, onChange }: ColumnPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (key: string) => {
    const next = selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key];
    if (next.length > 0) onChange(next);
  };

  const categories: ColumnCategory[] = ['Opportunity', 'Contact', 'Referral'];

  return (
    <div className={classes.columnPicker} ref={ref}>
      <button type="button" className={classes.columnsButton} onClick={() => setOpen((v) => !v)}>
        Columns ({selected.length})
      </button>
      {open && (
        <div className={classes.columnDropdown}>
          <div className={classes.columnDropdownHeader}>
            <span>Select columns</span>
            <button
              type="button"
              className={classes.resetColumnsBtn}
              onClick={() => onChange(DEFAULT_COLUMNS)}
            >
              Reset
            </button>
          </div>
          {categories.map((cat) => {
            const cols = allColumns.filter((c) => c.category === cat);
            if (cols.length === 0) return null;
            return (
              <div key={cat} className={classes.columnGroup}>
                <div className={classes.columnGroupTitle}>{cat}</div>
                {cols.map((col) => (
                  <label key={col.key} className={classes.columnOption}>
                    <input
                      type="checkbox"
                      checked={selected.includes(col.key)}
                      onChange={() => toggle(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface OpportunitySearchProps {
  locationId: string;
  ssoToken: string;
}

function OpportunitySearchComponent({ locationId, ssoToken }: OpportunitySearchProps) {
  const [query, setQuery] = useState('');
  const [pipelineFilter, setPipelineFilter] = useState('');
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDef[]>([]);
  const [rawOpportunities, setRawOpportunities] = useState<ApiOpportunity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pipelinesLoading, setPipelinesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<string>('opp_created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedColumns, setSelectedColumns] = useState<string[]>(loadSavedColumns);

  // Build the full column list: static + dynamic custom fields
  const allColumns = useMemo(() => {
    const customCols = customFieldDefs.map(buildCustomFieldColumn);
    return [...STATIC_COLUMNS, ...customCols];
  }, [customFieldDefs]);

  const columnMap = useMemo(() => new Map(allColumns.map((c) => [c.key, c])), [allColumns]);

  const activeColumns = useMemo(
    () => selectedColumns.map((k) => columnMap.get(k)).filter(Boolean) as ColumnDef[],
    [selectedColumns, columnMap]
  );

  const handleColumnsChange = useCallback(
    (keys: string[]) => {
      setSelectedColumns(keys);
      saveColumns(keys);

      // If the active sort field is no longer visible, fall back to the first visible column
      // and reset sortDir to match the fallback column's default direction
      setSortField((prev) => {
        if (keys.includes(prev)) return prev;
        const fallback = keys[0] || 'opp_created';
        const fallbackCol = columnMap.get(fallback);
        const defaultDir = fallbackCol?.render === 'currency' || fallbackCol?.render === 'date' ? 'desc' : 'asc';
        setSortDir(defaultDir);
        return fallback;
      });
    },
    [columnMap]
  );

  // Strip any saved custom field column keys that no longer exist after custom fields load
  useEffect(() => {
    if (customFieldDefs.length === 0) return;
    setSelectedColumns((prev) => {
      const validKeys = prev.filter((k) => !k.startsWith('cf_') || customFieldDefs.some((cf) => `cf_${cf.id}` === k));
      if (validKeys.length === prev.length) return prev;
      saveColumns(validKeys.length > 0 ? validKeys : DEFAULT_COLUMNS);
      return validKeys.length > 0 ? validKeys : DEFAULT_COLUMNS;
    });
  }, [customFieldDefs]);

  // Load pipelines and custom fields
  useEffect(() => {
    if (!locationId || !ssoToken) return;

    const loadPipelines = async () => {
      setPipelinesLoading(true);
      try {
        const response = await Api.get('/ghl/embed/pipelines', { headers: { 'X-GHL-SSO': ssoToken } });
        const items = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : [];
        setPipelines(items.map((p: { id: string; name: string }) => ({ id: p.id, name: p.name })));
      } catch {
        // non-critical
      } finally {
        setPipelinesLoading(false);
      }
    };

    const loadCustomFields = async () => {
      try {
        const response = await Api.get('/ghl/embed/custom-fields', {
          params: { locationId },
          headers: { 'X-GHL-SSO': ssoToken },
        });
        const raw = response.data?.customFields || response.data?.data || response.data || [];
        const fields = Array.isArray(raw) ? raw : [];
        setCustomFieldDefs(
          fields.map((f: { id: string; name: string; fieldKey?: string; field_key?: string; objectType?: string; object_type?: string; model?: string }) => ({
            id: f.id,
            name: f.name,
            fieldKey: f.fieldKey || f.field_key || '',
            objectType: f.objectType || f.object_type || f.model || '',
          }))
        );
      } catch {
        // non-critical
      }
    };

    loadPipelines();
    loadCustomFields();
  }, [locationId, ssoToken]);

  const handleSearch = useCallback(async () => {
    if (!locationId || !ssoToken) return;

    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    setPage(1);

    try {
      const params: Record<string, string | number> = {
        limit: 500,
        include: 'contact,pipeline,stage',
      };
      if (query.trim()) params['filter[search]'] = query.trim();
      if (pipelineFilter) params.pipeline_id = pipelineFilter;

      const response = await Api.get('/ghl/embed/opportunities', {
        params,
        headers: { 'X-GHL-SSO': ssoToken },
      });

      const items = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      setRawOpportunities(items);
    } catch (searchError) {
      // eslint-disable-next-line no-console
      console.error('Failed to search opportunities', searchError);
      setError('Unable to search opportunities. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [locationId, ssoToken, query, pipelineFilter]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const handleClear = useCallback(() => {
    setQuery('');
    setPipelineFilter('');
    setRawOpportunities([]);
    setHasSearched(false);
    setError(null);
    setPage(1);
  }, []);

  // Flatten rows using the full column list so all values are available for sorting
  const rows = useMemo(
    () => rawOpportunities.map((opp) => flattenOpportunity(opp, allColumns)),
    [rawOpportunities, allColumns]
  );

  // Sort
  const sortedRows = useMemo(() => {
    const col = columnMap.get(sortField);
    if (!col) return rows;

    return [...rows].sort((a, b) => {
      const va = a[sortField];
      const vb = b[sortField];
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va ?? '').localeCompare(String(vb ?? ''));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortField, sortDir, columnMap]);

  // Pagination
  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);
  const paginatedRows = useMemo(
    () => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedRows, page]
  );

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        const col = columnMap.get(field);
        setSortDir(col?.render === 'currency' || col?.render === 'date' ? 'desc' : 'asc');
      }
      setPage(1);
    },
    [sortField, columnMap]
  );

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return <span className={classes.sortIcon}>{'\u2195'}</span>;
    return (
      <span className={`${classes.sortIcon} ${classes.sortIconActive}`}>
        {sortDir === 'asc' ? '\u2191' : '\u2193'}
      </span>
    );
  };

  // Render a cell value based on render hint
  const renderCell = (col: ColumnDef, row: FlatRow) => {
    const val = row[col.key];
    const strVal = String(val ?? '\u2014');

    switch (col.render) {
      case 'currency':
        return formatCurrency(Number(val ?? 0));

      case 'date':
        return formatDate(strVal);

      case 'status':
        return (
          <span className={`badge ${classes.statusBadge} ${classes[`status${strVal}`] || ''}`}>
            {strVal}
          </span>
        );

      case 'badge':
        return col.key.includes('stage') ? (
          <span className={classes.stageBadge}>{strVal}</span>
        ) : (
          <span className={classes.pipelineBadge}>{strVal}</span>
        );

      case 'link-opp': {
        const locId = row._locationId || locationId;
        if (locId && row._id) {
          return (
            <a href={buildGhlOpportunityUrl(locId, row._id)} target="_blank" rel="noopener noreferrer" className={classes.oppLink}>
              {strVal}
            </a>
          );
        }
        return strVal;
      }

      case 'link-contact': {
        const locId = row._locationId || locationId;
        if (locId && row._contactId) {
          return (
            <a href={buildGhlContactUrl(locId, row._contactId)} target="_blank" rel="noopener noreferrer" className={classes.oppLink}>
              {strVal}
            </a>
          );
        }
        return strVal;
      }

      default:
        return strVal;
    }
  };

  const renderPageButtons = () => {
    if (totalPages <= 1) return null;

    const buttons: React.ReactNode[] = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);
    startPage = Math.max(1, endPage - maxVisible + 1);

    buttons.push(
      <button key="prev" type="button" className={classes.pageButton} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
        &laquo;
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          type="button"
          className={`${classes.pageButton} ${page === i ? classes.pageButtonActive : ''}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button key="next" type="button" className={classes.pageButton} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
        &raquo;
      </button>
    );

    return buttons;
  };

  // Total value from all results
  const totalValue = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.opp_value ?? 0), 0),
    [rows]
  );

  return (
    <div className={classes.container}>
      <div className={classes.searchSection}>
        <div className={classes.searchHeader}>
          <h4 className={classes.searchTitle}>Opportunity Search</h4>
          <p className={classes.searchSubtitle}>
            Search across all pipelines by opportunity name, contact name, email, or phone.
          </p>
        </div>

        <div className={classes.searchForm}>
          <div className={classes.fieldGroup}>
            <label className={classes.fieldLabel} htmlFor="opp-search-query">
              Search
            </label>
            <input
              id="opp-search-query"
              type="text"
              className={classes.searchInput}
              placeholder="Name, contact, email, phone..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className={classes.fieldGroup} style={{ flex: '0 0 200px', minWidth: '160px' }}>
            <label className={classes.fieldLabel} htmlFor="opp-search-pipeline">
              Pipeline
            </label>
            <select
              id="opp-search-pipeline"
              className={classes.pipelineSelect}
              value={pipelineFilter}
              onChange={(e) => setPipelineFilter(e.target.value)}
              disabled={pipelinesLoading}
            >
              <option value="">All pipelines</option>
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <button type="button" className={classes.searchButton} onClick={handleSearch} disabled={isLoading}>
            {isLoading ? 'Searching...' : 'Search'}
          </button>

          {hasSearched && (
            <button type="button" className={classes.clearButton} onClick={handleClear}>
              Clear
            </button>
          )}

          <ColumnPicker allColumns={allColumns} selected={selectedColumns} onChange={handleColumnsChange} />
        </div>
      </div>

      {error && <div className={`alert alert-danger ${classes.errorAlert}`}>{error}</div>}

      {isLoading && (
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Searching...</span>
          </div>
          <p>Searching opportunities...</p>
        </div>
      )}

      {!isLoading && hasSearched && rows.length === 0 && (
        <div className={classes.emptyState}>
          <div className={classes.emptyIcon}>&#128269;</div>
          <p className={classes.emptyTitle}>No opportunities found</p>
          <p className={classes.emptyText}>Try adjusting your search terms or pipeline filter.</p>
        </div>
      )}

      {!isLoading && rows.length > 0 && (
        <div className={classes.resultsCard}>
          <div className={classes.resultsHeader}>
            <h5 className={classes.resultsTitle}>
              {sortedRows.length} {sortedRows.length === 1 ? 'result' : 'results'}
            </h5>
            <span className={classes.resultsMeta}>{formatCurrency(totalValue)} total value</span>
          </div>

          <div className={classes.tableWrapper}>
            <table className={`table table-hover ${classes.table}`}>
              <thead>
                <tr>
                  {activeColumns.map((col) => (
                    <th key={col.key} onClick={() => handleSort(col.key)}>
                      {col.label} {renderSortIcon(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row) => (
                  <tr key={row._id}>
                    {activeColumns.map((col) => (
                      <td key={col.key}>{renderCell(col, row)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={classes.pagination}>
              <span className={classes.pageInfo}>
                Showing {(page - 1) * PAGE_SIZE + 1}&ndash;
                {Math.min(page * PAGE_SIZE, sortedRows.length)} of {sortedRows.length}
              </span>
              <div className={classes.pageButtons}>{renderPageButtons()}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const OpportunitySearchMemo = memo(OpportunitySearchComponent, areEqual);

export { OpportunitySearchMemo as OpportunitySearchContainer };
